import { loadItemReadingPaneForDeeplink } from '../actions/loadItemReadingPaneForDeeplink';
import '../orchestrators/closeDeeplinkReadingPaneOrchestrator';
import isPopoutV2 from '../utils/isPopoutV2';
import { isSmimeAttachment } from 'owa-attachment-model-store';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isPublicFolder } from 'owa-folders';
import { lazyGetGroupDetailsAction } from 'owa-groups-shared-actions';
import { convertRestIdToEwsId } from 'owa-identifiers';
import { getMailboxInfoFromFolderId } from 'owa-mail-mailboxinfo/lib/getMailboxInfo';
import initializeCLPStore from 'owa-mail-protection/lib/actions/clp/initializeCLPStore';
import isRoamingSignatureEnabled from 'owa-roaming-signature-option/lib/utils/isRoamingSignatureEnabled';
import setRoamingSignatureStore from 'owa-mail-signature/lib/actions/setRoamingSignatureStore';
import getDefaultSignatureStore from 'owa-mail-signature/lib/utils/getDefaultSignatureStore';
import readingPaneStore from 'owa-mail-reading-pane-store/lib/store/Store';
import getItemIdFromInternetMessageId from 'owa-mail-reading-pane-store/lib/utils/getItemIdFromInternetMessageId';
import getDataFromParentWindow from 'owa-popout-v2/lib/utils/getDataFromParentWindow';
import isSendAsAliasEnabled from 'owa-proxy-address-option/lib/utils/isSendAsAliasEnabled';
import { getQueryStringParameter, hasQueryStringParameter } from 'owa-querystring';
import { lazyOnSmimeItemAttachmentOpened } from 'owa-smime';

import {
    ITEMID_QUERY_PARAM,
    ISDISCOVERYVIEW_QUERY_PARAM,
    IMMERSIVEVIEW_QUERY_PARAM,
    PRINT_QUERY_PARAM,
} from '../utils/constants';
import { getGroupMailboxInfo, ClientItemId } from 'owa-client-ids';
import {
    lazyLoadConversationReadingPane,
    lazyGetLatestNonDraftItemIdFromConversation,
} from 'owa-mail-reading-pane-store';
import type ReadingPanePopoutItemFolderInfo from 'owa-popout-utils/lib/schema/ReadingPanePopoutItemFolderInfo';
import {
    selectDefaultFolder,
    lazyLoadPopoutDataToFolderStore,
    lazyLoadPopoutDataToPublicFolderFavoriteStore,
    selectFolder,
} from 'owa-mail-folder-forest-actions';

export interface MailModuleRouteParamaters {
    itemId?: string;
    readonlyItemId?: string;
    base64EncodedInternetMessageId?: string;
    convId?: string;
    groupDomain?: string;
    groupAlias?: string;
}

function getMailBoxInfo(parameters: MailModuleRouteParamaters, folderId: string) {
    if (parameters.groupDomain && parameters.groupAlias) {
        return getGroupMailboxInfo(`${parameters.groupAlias}@${parameters.groupDomain}`);
    }

    return getMailboxInfoFromFolderId(folderId);
}

export async function deepLinkMailModuleRouteHandler(parameters: MailModuleRouteParamaters) {
    const popoutData = await getDataFromParentWindow<ReadingPanePopoutItemFolderInfo>();
    readingPaneStore.isFluidEnabledOverride = popoutData?.includeFluidOnPopout;
    const instrumentationContext = popoutData?.instrumentationContext;
    const protectionStore = popoutData?.protectionStore;

    let { itemId, readonlyItemId, convId, base64EncodedInternetMessageId } = parameters;
    let isDiscoveryView = false;
    // If the URL is redirected due to a logOnIndex
    // The path may be url decoded and the item id in the route params may be incorrect
    // If there is an ItemID query param decode it and use it
    // i.e. from mail/deeplink to mail/0/deeplink
    if (hasQueryStringParameter(ITEMID_QUERY_PARAM)) {
        itemId = decodeURIComponent(getQueryStringParameter(ITEMID_QUERY_PARAM));
    }

    if (hasQueryStringParameter(ISDISCOVERYVIEW_QUERY_PARAM)) {
        isDiscoveryView = getQueryStringParameter(ISDISCOVERYVIEW_QUERY_PARAM) == '1';
    }

    // Since S/MIME attachments have "smime-" prefix and the parentFolderId is null, we need not convert the itemId
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2322 (81,5): Type 'string | null | undefined' is not assignable to type 'string | undefined'.
    // @ts-expect-error
    itemId = isSmimeAttachment(itemId) ? itemId : convertRestIdToEwsId(itemId);
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2322 (85,5): Type 'string | null' is not assignable to type 'string | undefined'.
    // @ts-expect-error
    convId = convertRestIdToEwsId(convId);

    if (readonlyItemId) {
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2322 (91,9): Type 'string | null' is not assignable to type 'string | undefined'.
        // @ts-expect-error
        itemId = convertRestIdToEwsId(readonlyItemId);
    }

    if (popoutData?.folderId) {
        if (isPublicFolder(popoutData.folderId)) {
            await lazyLoadPopoutDataToPublicFolderFavoriteStore.importAndExecute(popoutData);
        } else {
            await lazyLoadPopoutDataToFolderStore.importAndExecute(popoutData);
        }
        selectFolder(popoutData.folderId, popoutData.treeType, 'DeepLink' /* ActionSource */);
    } else {
        // If popout, don't select default folder since selecting default folder loads initial
        // table view(FindConversation) and prefetches First N (GetConversationItems or GetItem).
        !isPopoutV2() && selectDefaultFolder('DeepLink');
    }
    const mailboxInfo = getMailBoxInfo(parameters, popoutData?.folderId);
    if (popoutData?.smimeAttachmentItem) {
        // populate the mailStore with the SmimeItem so that loadItemReadingPane
        // does not try to fetch it from server
        await lazyOnSmimeItemAttachmentOpened.importAndExecute(
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2345 (114,13): Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
            // @ts-expect-error
            itemId,
            popoutData.smimeAttachmentItem,
            mailboxInfo
        );
    }

    if (isFeatureEnabled('cmp-clp') && protectionStore) {
        const clpStore = {
            clpLabels: protectionStore.clpLabels,
            learnMoreUrl: protectionStore.learnMoreUrl,
            hasMandatoryLabel: protectionStore.hasMandatoryLabel,
        };
        initializeCLPStore(clpStore);
    }

    if (popoutData?.signatureStore?.roamingSignatureMap) {
        const signaturesObject = popoutData?.signatureStore?.roamingSignatureMap as Object;
        const roamingSignatureMap = getDefaultSignatureStore().roamingSignatureMap;
        Object.entries(signaturesObject).forEach(([key, value]) => {
            roamingSignatureMap.set(key, value);
        });

        popoutData.signatureStore.roamingSignatureMap = roamingSignatureMap;
    }

    const signatureStore = popoutData?.signatureStore;
    if (isRoamingSignatureEnabled() && signatureStore) {
        setRoamingSignatureStore(signatureStore);
    }

    if (!itemId) {
        if (convId) {
            const loadConversationReadingPane = await lazyLoadConversationReadingPane.import();

            const conversationId: ClientItemId = {
                Id: convId,
                mailboxInfo: mailboxInfo,
            };
            await loadConversationReadingPane(
                conversationId,
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2345 (157,17): Argument of type 'InstrumentationContext | undefined' is not assignable to parameter of type 'InstrumentationContext'.
                // @ts-expect-error
                instrumentationContext,
                null /*conversation.subject*/
            );

            const getLatestNonDraftItemIdFromConversation = await lazyGetLatestNonDraftItemIdFromConversation.import();
            const latestMessageId = await getLatestNonDraftItemIdFromConversation(conversationId);
            if (latestMessageId) {
                itemId = latestMessageId.Id;
            }
        } else if (base64EncodedInternetMessageId) {
            itemId = await getItemIdFromInternetMessageId(
                decodeURIComponent(atob(base64EncodedInternetMessageId))
            );
        }
    }

    if (itemId) {
        loadItemReadingPaneForDeeplink(itemId, mailboxInfo, {
            isReadOnly: !!readonlyItemId,
            isDiscoveryView: isDiscoveryView,
            isImmersiveView: hasQueryStringParameter(IMMERSIVEVIEW_QUERY_PARAM),
            isPrint: hasQueryStringParameter(PRINT_QUERY_PARAM),
        });
    }

    const useProxyEmail = getQueryStringParameter('useProxyEmail');
    if (isSendAsAliasEnabled() && mailboxInfo.type == 'GroupMailbox' && useProxyEmail) {
        const { mailboxSmtpAddress } = mailboxInfo;
        await lazyGetGroupDetailsAction.importAndExecute(mailboxSmtpAddress);
    }
}
